/**
 * The one place that decides what an AI refusal SAYS.
 *
 * A 429 from the AI endpoints means one of two completely different things and
 * the copy used to conflate them: FREE sets `aiTokenMonthlyLimit: 0`, so every
 * AI call on a Free org fails on the very first request and the user was told
 * to "ask an admin to raise the budget" - a budget no admin can raise, because
 * the plan has no AI at all. Only a non-zero limit is a real budget that ran
 * out.
 *
 * Pure and free of React so both the chat widget and the profile page read the
 * same branch, and so the branch is testable without rendering either.
 */

export interface AiLimitCopy {
  /** The sentence to show. */
  text: string;
  /** Set only when the viewer can act on it themselves (admins). */
  cta?: { label: string; to: string };
  /** True when the plan has no AI at all, as opposed to a spent budget. */
  notIncluded: boolean;
}

export const BILLING_SETTINGS_PATH = '/settings?tab=billing';

/**
 * @param aiTokenMonthlyLimit `getOrgLimits(...).aiTokenMonthlyLimit` - `0` means
 *   the plan includes no AI, `null` means unlimited, a number means a budget.
 * @param isAdmin whether the viewer can reach billing themselves.
 */
export function aiLimitCopy(
  aiTokenMonthlyLimit: number | null | undefined,
  isAdmin: boolean
): AiLimitCopy {
  if (aiTokenMonthlyLimit === 0) {
    return {
      notIncluded: true,
      text: isAdmin
        ? 'AI features are not included on your plan.'
        : 'AI features are not included on your plan. Ask an admin about upgrading.',
      cta: isAdmin ? { label: 'View plans', to: BILLING_SETTINGS_PATH } : undefined,
    };
  }
  return {
    notIncluded: false,
    text: isAdmin
      ? 'Monthly AI usage limit reached. Raise the budget in AI settings or try again next month.'
      : 'Monthly AI usage limit reached. Ask your admin to raise the budget.',
  };
}
